// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react'
import type { AdminPanelRefresh, AdminStatus, PanelName } from '@tunasong/schemas'
import { adminPanelRefreshSchema, adminStatusSchema, panelNames } from '@tunasong/schemas'
import { logger } from '@tunasong/models'
import baseQuery from './base-query.js'

/** What the page shows in each panel when the response can't be read: never a blank page (admin spec §9) */
const unreadable = {
  state: 'unavailable',
  reason: 'unavailable',
  detail: 'The status response could not be read.',
} as const

const unreadableStatus = (): AdminStatus => ({
  generatedAt: new Date().toISOString(),
  panels: {
    aws: unreadable,
    sentry: unreadable,
    deploy: unreadable,
    posthog: unreadable,
  },
})

/** Parses `GET admin/status`; a body the schema rejects becomes every panel unavailable */
export const parseAdminStatus = (body: unknown): AdminStatus => {
  const parsed = adminStatusSchema.safeParse(body)
  if (parsed.success) {
    return parsed.data
  }
  logger.warn(`Admin status response failed the schema; showing all ${panelNames.length} panels unavailable`)
  return unreadableStatus()
}

/** Parses `GET admin/status?panel=…`; a body the schema rejects becomes that panel unavailable */
export const parseAdminPanel = (body: unknown, panel: PanelName): AdminPanelRefresh => {
  const parsed = adminPanelRefreshSchema.safeParse(body)
  if (parsed.success) {
    return parsed.data
  }
  logger.warn(`Admin status response for ${panel} failed the schema; showing it unavailable`)
  return { generatedAt: new Date().toISOString(), panels: { [panel]: unreadable } }
}

/**
 * The admin status page's API (EPIC-31, admin spec §3.4). It sends the access token, which carries the
 * `tunasong-api/admin` scope the route requires; every other API keeps the ID token.
 */
export const adminApi = createApi({
  reducerPath: 'admin-api',
  baseQuery,
  endpoints: builder => ({
    getStatus: builder.query<AdminStatus, void>({
      query: () => ({ url: 'admin/status' }),
      extraOptions: { token: 'access' },
      transformResponse: parseAdminStatus,
    }),
    /** One panel refetched, for its Try again (Joni §3); the page merges it in */
    getPanel: builder.query<AdminPanelRefresh, PanelName>({
      query: panel => ({ url: 'admin/status', params: { panel } }),
      extraOptions: { token: 'access' },
      transformResponse: (body: unknown, _meta, panel) => parseAdminPanel(body, panel),
    }),
  }),
})
